import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UserButton } from "@clerk/react";
import { getMe } from "@workspace/api-client-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ShieldCheck, AlertTriangle, Lock, User } from "lucide-react";
import QuoteCarousel from "@/components/QuoteCarousel";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

type IdentityMeExtra = {
  identityCollectedAt?: string | null;
};

export default function IdentityCollection() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const meExt = me as (typeof me & IdentityMeExtra) | undefined;

  const [form, setForm] = useState({
    legalFirstName: "",
    legalLastName: "",
    dateOfBirth: "",
    addressLine1: "",
    addressCity: "",
    addressState: "",
    addressZip: "",
  });
  const [submitting, setSubmitting] = useState(false);

  function setField(key: keyof typeof form, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  const canSubmit =
    form.legalFirstName.trim() &&
    form.legalLastName.trim() &&
    form.dateOfBirth.trim() &&
    form.addressLine1.trim() &&
    form.addressCity.trim() &&
    form.addressState.trim() &&
    form.addressZip.trim();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const resp = await fetch("/api/users/me/identity", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await resp.json() as {
        identityCollectedAt?: string;
        error?: string;
      };

      if (!resp.ok) {
        toast({ title: "Submission failed", description: data.error ?? "Please try again.", variant: "destructive" });
        setSubmitting(false);
        return;
      }

      await qc.invalidateQueries({ queryKey: ["me"] });
      navigate("/debtor");
    } catch {
      toast({ title: "Network error", description: "Could not submit your identity. Please try again.", variant: "destructive" });
      setSubmitting(false);
    }
  }

  if (meExt?.identityCollectedAt) {
    navigate("/debtor");
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted flex flex-col">
      <header className="flex items-center justify-between px-6 py-4 border-b border-border/40">
        <Logo iconSize={32} />
        <UserButton />
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-xl bg-blue-100">
              <User className="h-6 w-6 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Verify Your Identity</h1>
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            To protect against fraud and comply with IRS nonprofit regulations, we collect your legal name, date of birth, and address. This information is encrypted and never shared with creditors.
          </p>

          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 flex gap-3 mb-6">
            <Lock className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-900">
              Your information is encrypted at rest and used only to verify your eligibility for debt relief. It will never appear on any public record or be shared with creditors.
            </p>
          </div>

          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="legalFirstName">Legal First Name <span className="text-red-500">*</span></Label>
                <Input
                  id="legalFirstName"
                  value={form.legalFirstName}
                  onChange={(e) => setField("legalFirstName", e.target.value)}
                  placeholder="Jane"
                  required
                  disabled={submitting}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="legalLastName">Legal Last Name <span className="text-red-500">*</span></Label>
                <Input
                  id="legalLastName"
                  value={form.legalLastName}
                  onChange={(e) => setField("legalLastName", e.target.value)}
                  placeholder="Smith"
                  required
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="dateOfBirth">Date of Birth <span className="text-red-500">*</span></Label>
              <Input
                id="dateOfBirth"
                type="date"
                value={form.dateOfBirth}
                onChange={(e) => setField("dateOfBirth", e.target.value)}
                required
                disabled={submitting}
                max={new Date().toISOString().split("T")[0]}
              />
              <p className="text-xs text-muted-foreground">Used for identity verification only.</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="addressLine1">Street Address <span className="text-red-500">*</span></Label>
              <Input
                id="addressLine1"
                value={form.addressLine1}
                onChange={(e) => setField("addressLine1", e.target.value)}
                placeholder="123 Main St, Apt 4B"
                required
                disabled={submitting}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="addressCity">City <span className="text-red-500">*</span></Label>
                <Input
                  id="addressCity"
                  value={form.addressCity}
                  onChange={(e) => setField("addressCity", e.target.value)}
                  placeholder="Springfield"
                  required
                  disabled={submitting}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="addressState">State <span className="text-red-500">*</span></Label>
                <select
                  id="addressState"
                  value={form.addressState}
                  onChange={(e) => setField("addressState", e.target.value)}
                  required
                  disabled={submitting}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">Select…</option>
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="addressZip">ZIP Code <span className="text-red-500">*</span></Label>
              <Input
                id="addressZip"
                value={form.addressZip}
                onChange={(e) => setField("addressZip", e.target.value)}
                placeholder="12345"
                maxLength={10}
                required
                disabled={submitting}
              />
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex gap-2 text-sm text-amber-900">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <span>
                By submitting, you confirm that all information is accurate and belongs to you. False submissions may result in your account being suspended and reported to relevant authorities.
              </span>
            </div>

            <Button
              type="submit"
              className="w-full gap-2"
              disabled={!canSubmit || submitting}
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Verifying…</>
              ) : (
                <><ShieldCheck className="h-4 w-4" /> Submit Identity Verification</>
              )}
            </Button>
          </form>
        </div>
      </div>

      <QuoteCarousel />
    </div>
  );
}
